import { useState, type ChangeEvent, type KeyboardEvent } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError, type AnalyticsMetrics, exportAnalytics, getAnalyticsSummary, listPrograms, type AnalyticsQuery } from "@/api/client";
import { useSession } from "@/auth/session";
import { saveBlob } from "@/components/download";
import { Field, inputClass } from "@/components/form";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import type { AnalyticsSearch } from "@/routes/analytics";
import { AttentionCards } from "./AttentionCards";
import { IssuedChart } from "./IssuedChart";
import { PRESETS, lagosToday, presetRange } from "./ranges";
import { PeriodTable, ProgramTable, TotalsRow } from "./ReportTables";

const analyticsApi = getRouteApi("/staff/analytics");

const isZero = (m: AnalyticsMetrics) =>
  m.issued + m.reissued + m.revoked + m.emails_sent + m.emails_failed + m.printed === 0 && m.payments.length === 0;

const DATE_RE = /^(\d{4})-\d{2}-\d{2}$/;
const validDate = (v: string) => {
  const m = DATE_RE.exec(v);
  return m !== null && Number(m[1]) >= 1900 && !Number.isNaN(Date.parse(v));
};

const H2 = "text-sm font-semibold text-zinc-950";

export function AnalyticsPage() {
  const search = analyticsApi.useSearch();
  const navigate = useNavigate();
  const { can } = useSession();

  const query: AnalyticsQuery = { from: search.from, to: search.to, group: search.group, programId: search.program };
  const summary = useQuery({
    queryKey: ["analytics", search],
    queryFn: () => getAnalyticsSummary(query),
    placeholderData: keepPreviousData,
  });
  const programs = useQuery({ queryKey: ["programs"], queryFn: listPrograms });
  const data = summary.data;

  const exportCsv = useMutation({
    mutationFn: async () => {
      const blob = await exportAnalytics(query);
      saveBlob(blob, `cipm-analytics-${data?.range.from}-${data?.range.to}.csv`);
    },
  });

  const go = (next: Partial<AnalyticsSearch>, replace = false) =>
    void navigate({ to: "/analytics", search: { ...search, ...next }, replace });

  // Drafts keep keystrokes local; only a full valid date (or a cleared field) reaches the URL.
  const [drafts, setDrafts] = useState<{ from?: string; to?: string }>({});
  const commitDate = (key: "from" | "to") => {
    const draft = drafts[key];
    setDrafts((d) => ({ ...d, [key]: undefined }));
    if (draft === undefined || draft === (search[key] ?? "")) return;
    if (draft === "") go({ [key]: undefined }, true);
    else if (validDate(draft)) go({ [key]: draft }, true);
  };
  const dateInput = (key: "from" | "to") => ({
    value: drafts[key] ?? search[key] ?? data?.range[key] ?? "",
    onChange: (e: ChangeEvent<HTMLInputElement>) => setDrafts((d) => ({ ...d, [key]: e.target.value })),
    onBlur: () => commitDate(key),
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") commitDate(key);
    },
  });
  const rangeError = summary.error instanceof ApiError && summary.error.status === 422 ? summary.error : null;
  const group = search.group ?? data?.range.group ?? "month";
  // Placeholder data was fetched for the previous grouping; label it as such.
  const dataGroup = data?.range.group ?? group;

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Analytics"
        detail="Certificates, deliveries and payments over a date range."
        action={
          <ToolbarButton disabled={!data || summary.isPlaceholderData || exportCsv.isPending} onClick={() => exportCsv.mutate()}>
            {exportCsv.isPending ? "Exporting..." : "Export CSV"}
          </ToolbarButton>
        }
      />
      <ErrorBanner error={exportCsv.error} />

      {data ? (
        <div className="space-y-2">
          <h2 className={H2}>Needs attention</h2>
          <AttentionCards attention={data.attention} can={can} />
        </div>
      ) : null}

      <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <ToolbarButton key={p.id} onClick={() => go(presetRange(p.id, lagosToday()))}>
              {p.label}
            </ToolbarButton>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="From">
            {(props) => (
              <input {...props} type="date" {...dateInput("from")} className={inputClass} />
            )}
          </Field>
          <Field label="To">
            {(props) => (
              <input {...props} type="date" {...dateInput("to")} className={inputClass} />
            )}
          </Field>
          <Field label="Group by">
            {(props) => (
              <select {...props} value={group} onChange={(e) => go({ group: e.target.value as AnalyticsSearch["group"] })} className={inputClass}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            )}
          </Field>
          <Field label="Program">
            {(props) => (
              <select {...props} value={search.program ?? ""} onChange={(e) => go({ program: e.target.value || undefined })} className={inputClass}>
                <option value="">All programs</option>
                {(programs.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        {rangeError ? (
          <p role="alert" className="text-sm text-red-700">
            {rangeError.message}
          </p>
        ) : null}
        <ErrorBanner error={programs.error} />
      </div>
      {summary.isLoading ? <EmptyState title="Loading analytics" detail="Fetching the report." /> : null}
      {!rangeError ? <ErrorBanner error={summary.error} /> : null}

      {data && !rangeError ? (
        data.buckets.every(isZero) && isZero(data.totals) ? (
          <EmptyState title="No activity in this range." detail="Try a wider range or another program." />
        ) : (
          <>
            <div className="space-y-2">
              <h2 className={H2}>Totals</h2>
              <TotalsRow totals={data.totals} />
            </div>
            <div className="space-y-2">
              <h2 className={H2}>Issued per period</h2>
              <IssuedChart buckets={data.buckets} group={dataGroup} />
            </div>
            <div className="space-y-2">
              <h2 className={H2}>By period</h2>
              <PeriodTable buckets={data.buckets} group={dataGroup} />
            </div>
            <div className="space-y-2">
              <h2 className={H2}>By program</h2>
              {data.by_program.length === 0 ? (
                <EmptyState title="No programs had activity in this range." detail="" compact />
              ) : (
                <ProgramTable rows={data.by_program} />
              )}
            </div>
          </>
        )
      ) : null}
    </section>
  );
}
